import React, { useState } from 'react';

// Symbol categories with display label and LaTeX
const SYMBOLS = {
  greek: {
    label: 'Greek',
    symbols: [
      // Lowercase
      { display: 'α', latex: '\\alpha' },
      { display: 'β', latex: '\\beta' },
      { display: 'γ', latex: '\\gamma' },
      { display: 'δ', latex: '\\delta' },
      { display: 'ε', latex: '\\epsilon' },
      { display: 'ζ', latex: '\\zeta' },
      { display: 'η', latex: '\\eta' },
      { display: 'θ', latex: '\\theta' },
      { display: 'ι', latex: '\\iota' },
      { display: 'κ', latex: '\\kappa' },
      { display: 'λ', latex: '\\lambda' },
      { display: 'μ', latex: '\\mu' },
      { display: 'ν', latex: '\\nu' },
      { display: 'ξ', latex: '\\xi' },
      { display: 'ο', latex: 'o', label: 'Omicron' },
      { display: 'π', latex: '\\pi' },
      { display: 'ρ', latex: '\\rho' },
      { display: 'σ', latex: '\\sigma' },
      { display: 'τ', latex: '\\tau' },
      { display: 'υ', latex: '\\upsilon' },
      { display: 'φ', latex: '\\phi' },
      { display: 'χ', latex: '\\chi' },
      { display: 'ψ', latex: '\\psi' },
      { display: 'ω', latex: '\\omega' },
      // Uppercase
      { display: 'Γ', latex: '\\Gamma' },
      { display: 'Δ', latex: '\\Delta' },
      { display: 'Θ', latex: '\\Theta' },
      { display: 'Λ', latex: '\\Lambda' },
      { display: 'Ξ', latex: '\\Xi' },
      { display: 'Π', latex: '\\Pi' },
      { display: 'Σ', latex: '\\Sigma' },
      { display: 'Υ', latex: '\\Upsilon' },
      { display: 'Φ', latex: '\\Phi' },
      { display: 'Ψ', latex: '\\Psi' },
      { display: 'Ω', latex: '\\Omega' },
    ]
  },
  accents: {
    label: 'Accents',
    symbols: [
      { display: 'x⃗', latex: '\\vec{}', cursor: -1, label: 'Vector arrow' },
      { display: 'x̂', latex: '\\hat{}', cursor: -1, label: 'Hat / unit vector' },
      { display: 'x̄', latex: '\\bar{}', cursor: -1, label: 'Bar' },
      { display: 'ẋ', latex: '\\dot{}', cursor: -1, label: 'Dot (1st derivative)' },
      { display: 'ẍ', latex: '\\ddot{}', cursor: -1, label: 'Double dot (2nd derivative)' },
      { display: 'x̃', latex: '\\tilde{}', cursor: -1, label: 'Tilde' },
      { display: 'AB→', latex: '\\overrightarrow{}', cursor: -1, label: 'Long vector arrow' },
      { display: 'x̄', latex: '\\overline{}', cursor: -1, label: 'Overline' },
      { display: 'x̂', latex: '\\widehat{}', cursor: -1, label: 'Wide hat' },
      { display: 'x̃', latex: '\\widetilde{}', cursor: -1, label: 'Wide tilde' },
    ]
  },
  operators: {
    label: 'Operators',
    symbols: [
      { display: '±', latex: '\\pm' },
      { display: '×', latex: '\\times' },
      { display: '÷', latex: '\\div' },
      { display: '·', latex: '\\cdot' },
      { display: '√', latex: '\\sqrt{}', cursor: -1 },
      { display: '∛', latex: '\\sqrt[3]{}', cursor: -1 },
      { display: '∫', latex: '\\int' },
      { display: '∮', latex: '\\oint' },
      { display: '∑', latex: '\\sum' },
      { display: '∏', latex: '\\prod' },
      { display: '∂', latex: '\\partial' },
      { display: '∇', latex: '\\nabla' },
    ]
  },
  relations: {
    label: 'Relations',
    symbols: [
      { display: '≤', latex: '\\leq' },
      { display: '≥', latex: '\\geq' },
      { display: '≠', latex: '\\neq' },
      { display: '≈', latex: '\\approx' },
      { display: '≡', latex: '\\equiv' },
      { display: '∝', latex: '\\propto' },
      { display: '∈', latex: '\\in' },
      { display: '∉', latex: '\\notin' },
      { display: '⊂', latex: '\\subset' },
      { display: '⊃', latex: '\\supset' },
      { display: '∞', latex: '\\infty' },
      { display: '∅', latex: '\\emptyset' },
    ]
  },
  structures: {
    label: 'Structures',
    symbols: [
      { display: 'a/b', latex: '\\frac{}{}', cursor: -3, label: 'Fraction' },
      { display: 'x²', latex: '^{}', cursor: -1, label: 'Superscript' },
      { display: 'x₂', latex: '_{}', cursor: -1, label: 'Subscript' },
      { display: '√x', latex: '\\sqrt{}', cursor: -1, label: 'Square root' },
      { display: '∫ᵇₐ', latex: '\\int_{}^{}', cursor: -4, label: 'Definite integral' },
      { display: '∑ⁿᵢ', latex: '\\sum_{i=1}^{n}', label: 'Sum' },
      { display: 'lim', latex: '\\lim_{x \\to }', label: 'Limit' },
      { display: '(ⁿₖ)', latex: '\\binom{}{}', cursor: -3, label: 'Binomial' },
      { display: '|x|', latex: '\\left| \\right|', cursor: -8, label: 'Absolute' },
      { display: '⌈x⌉', latex: '\\lceil \\rceil', cursor: -7, label: 'Ceiling' },
      { display: '⌊x⌋', latex: '\\lfloor \\rfloor', cursor: -8, label: 'Floor' },
      { display: '[a b]', latex: '\\begin{bmatrix}  \\end{bmatrix}', cursor: -14, label: 'Matrix' },
    ]
  },
  arrows: {
    label: 'Arrows',
    symbols: [
      { display: '→', latex: '\\rightarrow' },
      { display: '←', latex: '\\leftarrow' },
      { display: '↔', latex: '\\leftrightarrow' },
      { display: '⇒', latex: '\\Rightarrow' },
      { display: '⇐', latex: '\\Leftarrow' },
      { display: '⇔', latex: '\\Leftrightarrow' },
    ]
  }
};

const MathSymbolPalette = ({ onInsert, textareaRef }) => {
  const [activeTab, setActiveTab] = useState('greek');

  const handleInsert = (symbol) => {
    if (onInsert) {
      onInsert(symbol.latex, symbol.cursor);
    }

    // Focus back to textarea and position cursor
    if (textareaRef && textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  return (
    <div className="wb-symbol-palette">
      {/* Tabs */}
      <div className="wb-symbol-tabs">
        {Object.entries(SYMBOLS).map(([key, category]) => (
          <button
            key={key}
            className={`wb-symbol-tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Symbol Grid */}
      <div className="wb-symbol-grid">
        {SYMBOLS[activeTab].symbols.map((symbol, index) => (
          <button
            key={index}
            className="wb-symbol-btn"
            onClick={() => handleInsert(symbol)}
            title={symbol.label || symbol.latex}
          >
            {symbol.display}
          </button>
        ))}
      </div>
    </div>
  );
};

export default MathSymbolPalette;
